import { Phone, Mail, ArrowDown } from 'lucide-react';

/**
 * LpHero — Shared hero block for all PPC landing pages.
 * Brand-consistent with the rest of kgob.com (Playfair Display + slate palette).
 *
 * Provides ONE primary action (scroll to form) and TWO secondary actions
 * (clickable phone + clickable email) so visitors are never confused about
 * what to do next.
 */
export const LpHero = ({ config }) => {
  const scrollToForm = (e) => {
    e.preventDefault();
    const target = document.getElementById("lp-form");
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      className="relative overflow-hidden bg-gradient-to-br from-[#0B2240] via-[#102a4d] to-[#0B2240] text-white"
      data-testid="lp-hero"
    >
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, #C9A961 0%, transparent 40%), radial-gradient(circle at 80% 80%, #C9A961 0%, transparent 40%)",
        }}
      />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#C9A961]/15 border border-[#C9A961]/40 px-3 py-1 text-xs font-medium text-[#E8D49A] mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-[#C9A961] animate-pulse" />
            KGOB CPAs — Charlotte • Winston-Salem • Mooresville
          </div>

          <h1
            className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight tracking-tight mb-5"
            data-testid="lp-hero-h1"
          >
            {config.h1}
          </h1>

          <p className="text-base sm:text-lg text-white/80 max-w-2xl mb-8" data-testid="lp-hero-subhead">
            {config.subhead}
          </p>

          {/* Primary CTA + secondary contact pills */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <a
              href="#lp-form"
              onClick={scrollToForm}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C9A961] hover:bg-[#D4B975] text-[#0B2240] font-semibold px-6 py-3 text-sm sm:text-base transition min-h-[48px]"
              data-testid="lp-hero-primary-cta"
            >
              {config.cta}
              <ArrowDown className="h-4 w-4" />
            </a>

            <div className="flex flex-wrap gap-2 text-sm">
              <a
                href={`tel:${config.phone_dial}`}
                className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-4 py-2 hover:bg-white/15 transition min-h-[44px]"
                data-testid="lp-hero-phone"
              >
                <Phone className="h-4 w-4 text-[#E8D49A]" />
                <span className="font-medium">{config.phone_display}</span>
              </a>
              <a
                href={`mailto:${config.email}`}
                className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-4 py-2 hover:bg-white/15 transition min-h-[44px]"
                data-testid="lp-hero-email"
              >
                <Mail className="h-4 w-4 text-[#E8D49A]" />
                <span className="font-medium">{config.email}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
